import { useUserHasPermission } from '@/hooks/usePermissions';
import CreateEditEntityTemplateModal from '@/ui/components/createEditEntityTemplateModal/CreateEditEntityTemplateModal';
import { BLANK_ENTITY_TEMPLATES } from '@/ui/components/createEditEntityTemplateModal/blankEntityTemplates';
import { encodeAssignee } from '@/ui/components/createEditTaskSidebar/templateAssignee';
import {
  useTemplateAssigneeOptions,
  useUserCustomFieldLabelByIdentifier,
} from '@/ui/components/createEditTaskSidebar/useAssigneeOptions';
import {
  Badge,
  Button,
  ManageableItemsList,
  Modal,
  Tooltip,
} from '@payaca/ambrosia';
import { TemplatesPermissions } from '@payaca/permissions/templates/templates.permissions';
import {
  createTemplate,
  deleteTemplate,
  getListedTemplatesPage,
  updateTemplate,
} from '@payaca/store/templates/templatesActions';
import { PublicHydratedEntityTemplate } from '@payaca/types/entity-templates';
import {
  GetListedTemplatesQueryParams,
  ListedTemplatesPage,
  SortBy,
} from '@payaca/types/listedTemplateTypes';
import { SortDirection } from '@payaca/types/listViewTypes';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

type TaskTemplate = PublicHydratedEntityTemplate<'task'>;

const taskTypeLabel = (template: TaskTemplate): string => {
  if (template.data.customFieldIdentifiers?.length) return 'Form - Responsive';
  if (template.data.formTemplateId != null) return 'Form';
  return 'Checklist';
};

/**
 * Table view of the account's task templates. Rows open the edit sidebar
 * directly (there is no separate read-only view).
 */
const TaskTemplatesList: FC = () => {
  const dispatch = useDispatch();

  const canPersist = useUserHasPermission({
    permissions: [TemplatesPermissions.PERSIST_TEMPLATE],
  });
  const canDelete = useUserHasPermission({
    permissions: [TemplatesPermissions.DELETE_TEMPLATE],
  });

  const { options: assigneeOptions } = useTemplateAssigneeOptions();
  const customFieldLabels = useUserCustomFieldLabelByIdentifier();

  const [requestData, setRequestData] = useState<GetListedTemplatesQueryParams>(
    {
      pageNumber: 1,
      pageSize: 25,
      sortBy: SortBy.NAME,
      sortDirection: SortDirection.ASCENDING,
      entityTypes: ['task'],
    }
  );
  const [page, setPage] = useState<ListedTemplatesPage>();
  const [isLoading, setIsLoading] = useState(false);

  const [openModal, setOpenModal] = useState<
    | { type: 'create' }
    | { type: 'edit'; template: TaskTemplate }
    | { type: 'delete'; template: TaskTemplate }
    | null
  >(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>();

  const fetchPage = useCallback(
    (queryParams: GetListedTemplatesQueryParams) => {
      setIsLoading(true);
      dispatch(
        getListedTemplatesPage.request({
          queryParams,
          callback: (p: ListedTemplatesPage) => {
            setPage(p);
            setIsLoading(false);
          },
          onErrorCallback: () => setIsLoading(false),
        })
      );
    },
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- dispatch is stable
    []
  );

  useEffect(() => {
    fetchPage(requestData);
    // oxlint-disable-next-line react-hooks/exhaustive-deps -- fetchPage is stable
  }, [requestData]);

  const closeModal = () => {
    setOpenModal(null);
    setErrorMessage(undefined);
  };

  const renderAssignee = (template: TaskTemplate): ReactNode => {
    const assignee = template.data.assignee;
    if (!assignee) return '—';

    // A custom-field assignee's full path can be long once fieldsets nest, so
    // show just the leaf field with the full breadcrumb available on hover.
    if (assignee.type === 'userCustomField') {
      const display = customFieldLabels.get(assignee.customFieldIdentifier);
      const leafLabel =
        display?.leafLabel ??
        assignee.customFieldIdentifier.split('.').pop() ??
        assignee.customFieldIdentifier;
      const fullPath = display?.fullPath ?? assignee.customFieldIdentifier;

      return (
        <Tooltip tooltipContent={fullPath}>
          <span className="inline-flex items-center gap-2">
            <Badge variant="soft" colour="gray" size="sm">
              Custom field
            </Badge>
            {leafLabel}
          </span>
        </Tooltip>
      );
    }

    const encoded = encodeAssignee(assignee);
    return assigneeOptions.find((o) => o.value === encoded)?.label ?? '—';
  };

  const items = (page?.items ?? []).filter(
    (t): t is TaskTemplate => t.entityType === 'task'
  );

  return (
    <ManageableItemsList>
      <ManageableItemsList.HeaderBar
        buttons={
          <Tooltip
            enabled={!canPersist}
            tooltipContent="You don't have permission to perform this action"
          >
            <Button
              size="sm"
              disabled={!canPersist}
              onClick={
                canPersist ? () => setOpenModal({ type: 'create' }) : undefined
              }
            >
              Create task template
            </Button>
          </Tooltip>
        }
      />
      <ManageableItemsList.ActionBar>
        <ManageableItemsList.ActionBar.SearchInput
          value={requestData.searchTerm ?? ''}
          onChange={(searchTerm) =>
            setRequestData((rd) => ({ ...rd, pageNumber: 1, searchTerm }))
          }
          changeTimeoutMs={300}
        />
      </ManageableItemsList.ActionBar>
      <ManageableItemsList.Table
        emptyText="No templates have been added yet"
        uniqueKey="publicId"
        items={items}
        isLoading={isLoading}
        onClickRow={
          canPersist
            ? (item) => setOpenModal({ type: 'edit', template: item })
            : undefined
        }
        itemActions={[
          {
            label: 'Edit',
            isDisabled: () => !canPersist,
            disabledReasons: [
              "You don't have permission to perform this action",
            ],
            onClick: (item) => setOpenModal({ type: 'edit', template: item }),
          },
          {
            label: 'Delete',
            isDisabled: (item) => !canDelete || !!item.servicePlans?.length,
            disabledReasons: [
              "You don't have permission to perform this action",
              'This template is used by a service plan',
            ],
            onClick: (item) => setOpenModal({ type: 'delete', template: item }),
          },
        ]}
      >
        <ManageableItemsList.Table.Column
          header="Template name"
          field="name"
          render={(value) => value || <em>Untitled</em>}
        />
        <ManageableItemsList.Table.Column
          header="Task name"
          field="data"
          render={(data) => data?.name ?? ''}
        />
        <ManageableItemsList.Table.Column
          header="Type"
          field="publicId"
          render={(_, row) => taskTypeLabel(row as TaskTemplate)}
        />
        <ManageableItemsList.Table.Column
          header="Assignee"
          field="data"
          render={(_, row) => renderAssignee(row as TaskTemplate)}
        />
        <ManageableItemsList.Table.Column
          header="Deadline"
          field="data"
          render={(data) =>
            data?.deadlineOffsetDays != null
              ? `${data.deadlineOffsetDays} day${
                  data.deadlineOffsetDays === 1 ? '' : 's'
                }`
              : '—'
          }
        />
      </ManageableItemsList.Table>
      {page && (
        <ManageableItemsList.PaginationBar
          type="from-paginated-result"
          totalCount={page.totalItemCount}
          limit={page.pageSize}
          offset={(page.pageNumber - 1) * page.pageSize}
          onPageChange={(pageNumber) =>
            setRequestData((rd) => ({ ...rd, pageNumber }))
          }
        />
      )}

      {(openModal?.type === 'create' || openModal?.type === 'edit') && (
        <CreateEditEntityTemplateModal
          isOpen
          onClose={closeModal}
          action={openModal.type === 'create' ? 'Create' : 'Edit'}
          template={
            openModal.type === 'edit'
              ? openModal.template
              : BLANK_ENTITY_TEMPLATES.task
          }
          isProcessing={isProcessing}
          errorMessage={errorMessage}
          allowModifyTemplateNameDescription
          onRequestSave={(t) => {
            setIsProcessing(true);
            setErrorMessage(undefined);
            const onSuccess = () => {
              setIsProcessing(false);
              closeModal();
              fetchPage(requestData);
            };
            const onError = () => {
              setIsProcessing(false);
              setErrorMessage('Failed to save template. Please try again.');
            };
            if (openModal.type === 'edit') {
              dispatch(
                updateTemplate.request({
                  publicId: openModal.template.publicId,
                  template: t,
                  callback: onSuccess,
                  onErrorCallback: onError,
                })
              );
            } else {
              dispatch(
                createTemplate.request({
                  template: t,
                  callback: onSuccess,
                  onErrorCallback: onError,
                })
              );
            }
          }}
        />
      )}

      {openModal?.type === 'delete' && (
        <Modal title="Delete task template" isOpen onClose={closeModal}>
          <Modal.Body>
            <p className="mb-6 text-base">
              Are you sure you want to delete this task template?
            </p>
          </Modal.Body>
          <Modal.Footer>
            <Modal.Footer.Actions>
              <Button
                className="mr-2"
                variant="secondary"
                disabled={isProcessing}
                onClick={closeModal}
              >
                Keep
              </Button>
              <Button
                isProcessing={isProcessing}
                disabled={isProcessing}
                colour="negative"
                onClick={() => {
                  setIsProcessing(true);
                  dispatch(
                    deleteTemplate.request({
                      publicId: openModal.template.publicId,
                      callback: () => {
                        setIsProcessing(false);
                        closeModal();
                        fetchPage(requestData);
                      },
                      onErrorCallback: () => {
                        setIsProcessing(false);
                        setErrorMessage(
                          'Failed to delete template. Please try again.'
                        );
                      },
                    })
                  );
                }}
              >
                Delete
              </Button>
            </Modal.Footer.Actions>
          </Modal.Footer>
        </Modal>
      )}
    </ManageableItemsList>
  );
};

export default TaskTemplatesList;
